const KEY = 'kermes.deviceCode'
const DEFAULT_CODE = 'K1'

/**
 * A short human-assigned code for this till ("K1", "K2"). It prefixes every
 * receipt number, which is what keeps two offline tills from issuing the same
 * one. Deliberately a human decision: it matches the physical layout, so staff
 * can say "2 numaralı kasa" and mean it.
 */
export function getDeviceCode(): string {
  const stored = localStorage.getItem(KEY)
  if (stored) return stored
  localStorage.setItem(KEY, DEFAULT_CODE)
  return DEFAULT_CODE
}

const AUTOPRINT_KEY = 'kermes.autoPrint'

/**
 * Whether this till prints waiters' tickets as they arrive. Per device, not
 * per yurt: one laptop has the printer, another might just be watching.
 * Off by default so a new device never surprises anyone with paper.
 */
export function getAutoPrint(): boolean {
  return localStorage.getItem(AUTOPRINT_KEY) === 'on'
}

export function setAutoPrint(on: boolean): void {
  localStorage.setItem(AUTOPRINT_KEY, on ? 'on' : 'off')
}

/**
 * Notifies when the setting changes in another tab. The browser only fires
 * `storage` for *other* tabs, so a till with the app open twice would otherwise
 * show one switch on and the other off while both wrote to the same key.
 */
export function onAutoPrintChange(fn: (on: boolean) => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === AUTOPRINT_KEY) fn(e.newValue === 'on')
  }
  window.addEventListener('storage', handler)
  return () => window.removeEventListener('storage', handler)
}

/**
 * "Fişleri kasa bilgisayarından bas" (21 Eki 2026): bu cihazda yazıcı yok;
 * yapılan satışın fişleri basılmıyor, sipariş işaretleniyor ve otomatik
 * yazdırması açık kasa bilgisayarı basıyor.
 */
const PRINT_AT_TILL_KEY = 'adissoo.printAtTill'

export function getPrintAtTill(): boolean {
  try {
    return localStorage.getItem(PRINT_AT_TILL_KEY) === 'on'
  } catch {
    return false
  }
}

export function setPrintAtTill(on: boolean): void {
  try {
    localStorage.setItem(PRINT_AT_TILL_KEY, on ? 'on' : 'off')
  } catch {
    // Gizli pencere: bu oturumluk kalmıyor, zararı yok.
  }
}

const PRINTED_KEY = 'kermes.printedIds'
/**
 * Enough for a long service day, and bounded so the record can't grow without
 * limit. The oldest entries fall off first; a ticket from hours ago is settled
 * and gone from the open list, so it can't come back round to be reprinted.
 */
const PRINTED_MAX = 2000

/**
 * Which slips have already come off this printer.
 *
 * Kept in localStorage rather than in memory: the set used to live in a ref,
 * so reloading the page or walking to Menü and back rebuilt it empty and the
 * till printed every unpaid ticket again. Per device, because it describes what
 * this printer has physically produced, a second till has its own paper.
 */
export function getPrintedIds(): Set<string> {
  try {
    const raw = localStorage.getItem(PRINTED_KEY)
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed)
      ? new Set(parsed.filter((v): v is string => typeof v === 'string'))
      : new Set()
  } catch {
    // Corrupt or unreadable: start clean rather than break the till. The cost
    // is a duplicate slip, not a lost sale.
    return new Set()
  }
}

/** Records slips as printed. Returns the full set so callers can render it. */
export function addPrintedIds(ids: string[]): Set<string> {
  const next = getPrintedIds()
  for (const id of ids) {
    // Re-adding moves an id to the end, keeping it from being trimmed while
    // it's still in play.
    next.delete(id)
    next.add(id)
  }

  const all = [...next]
  const kept = all.length > PRINTED_MAX ? all.slice(all.length - PRINTED_MAX) : all
  try {
    localStorage.setItem(PRINTED_KEY, JSON.stringify(kept))
  } catch {
    // Storage full or blocked. The in-memory set still guards this session.
  }
  return new Set(kept)
}

export function setDeviceCode(code: string): string {
  const cleaned = code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4)
  const value = cleaned || DEFAULT_CODE
  localStorage.setItem(KEY, value)
  return value
}
